import { FC, useEffect, useRef, useState } from "react";
import { CircleAlert, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

import { IconCheckCircleFilled } from "@/components/_icons/IconCheckCircleFilled";
import { UploadUrl } from "@/components/_molecules/input-image/input-image-upload";
import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { createOperationClient } from "@/services/react-query.service/graphql-client";

import { createSignedUploadUrlMutationDocument } from "../hooks/api/signed-url.gql";

interface UploadResult {
  file: File;
  url: string;
  error?: Error | undefined;
}

export interface FileStorageUploaderProps {
  className?: string;
  file: File;
  ownerId: string;
  ownerType: string;
  onFileUploadFinished?: (result: UploadResult) => void;
}

export const FileStorageUploader: FC<FileStorageUploaderProps> = ({
  file,
  ownerId,
  ownerType,
  onFileUploadFinished,
}) => {
  const { t } = useTranslation("common");
  const effectTriggeredRef = useRef(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [error, setError] = useState<(Error & { isMalware?: boolean }) | null>(null);

  useEffect(() => {
    if (!effectTriggeredRef.current) {
      effectTriggeredRef.current = true;
      startFileUpload(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getSignedUploadUrl(): Promise<UploadUrl | null> {
    const fileExtension = file.name.split(".").pop() ?? "";

    const client = createOperationClient("createSignedUploadUrl");
    const data = await client.request(createSignedUploadUrlMutationDocument, {
      input: {
        fileExtension: fileExtension,
        type: file.type,
        ownerId,
        ownerType,
      },
    });

    if (!data) {
      return null;
    }

    return data.createSignedUploadUrl;
  }

  async function uploadFile(file: File, url: string) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.onload = () => {
        const status = xhr.status;
        if (status >= 200 && status < 300) {
          resolve(true);
        } else if (status === 403) {
          reject(new Error(`Upload failed: Access denied (HTTP ${status}). The upload URL may have expired. Please try again.`));
        } else if (status === 413) {
          reject(new Error(`Upload failed: File "${file.name}" is too large (HTTP ${status}). Please reduce the file size and try again.`));
        } else {
          reject(new Error(`Upload failed: Server responded with HTTP ${status}. Please try again later.`));
        }
      };
      xhr.onerror = () => {
        reject(new Error(`Upload failed: Network error while uploading "${file.name}". Please check your internet connection and try again.`));
      };
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.setRequestHeader("Cache-Control", "public, max-age=604800, immutable");
      xhr.send(file);
    });
  }

  async function startFileUpload(file: File) {
    if (!file) {
      return;
    }
    if (loading) {
      return;
    }
    if (completed) {
      return;
    }

    setLoading(true);

    let uploadUrl: UploadUrl | null;
    try {
      uploadUrl = await getSignedUploadUrl();
    } catch (e) {
      const cause = e instanceof Error ? e : new Error("The server may be temporarily unavailable.");
      const error = new Error(
        `Could not prepare upload for "${file.name}": ${cause.message}`,
        { cause },
      );
      setError(error);
      setLoading(false);
      setCompleted(true);
      onFileUploadFinished?.({ file, url: "", error });
      return;
    }

    if (!uploadUrl) {
      const error = new Error(
        `Could not prepare upload for "${file.name}". The server did not return a valid upload URL. Please try again later.`,
      );
      setError(error);
      setLoading(false);
      setCompleted(true);
      onFileUploadFinished?.({ file, url: "", error });
      return;
    }

    try {
      await uploadFile(file, uploadUrl.signedUrl);
      await onFileUploadFinished?.({ file, url: uploadUrl.permaUrl });
      setCompleted(true);
      setLoading(false);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(`Could not upload "${file.name}" to storage.`, { cause: e });
      setError(error as Error & { isMalware?: boolean });
      setCompleted(true);
      setLoading(false);
      onFileUploadFinished?.({ file, url: uploadUrl.permaUrl, error });
    }
  }

  function isMalwareError(error: Error & { isMalware?: boolean }): boolean {
    return error.isMalware === true || error.message?.includes("MALWARE_DETECTED");
  }

  function getErrorMessage(): string {
    if (error && isMalwareError(error)) {
      return t("documents.malwareDetected", { fileName: file.name });
    }
    return t("documents.uploadError", { fileName: file.name });
  }

  if (completed && error) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-destructive/10">
            <CircleAlert className="h-4 w-4 text-destructive" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{file.name}</p>
            <p className="mt-0.5 text-xs text-destructive">{getErrorMessage()}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <FileText className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm">{file.name}</p>
        </div>
        <div className="shrink-0">
          {!completed && <LoadingDots />}
          {completed && !error && <IconCheckCircleFilled className="h-4 w-4 text-green-500" />}
        </div>
      </div>
    </div>
  );
};
